import { Category, Product, User } from '../../types/api';

import { baseApi } from './baseApi';

// ==================== Categories Management ====================

export interface CreateCategoryRequest {
  name: string;
  nameEn: string;
  description?: string;
  descriptionEn?: string;
  price: number;
  cost: number;
  cashbackPercentage: number;
  isEnabledCashback?: boolean;
  isHidden?: boolean;
  isParent?: boolean;
  parentId?: string | null;
}

export interface CreateCategoryResponse {
  status: 'success';
  message: string;
  data: {
    category: Category;
  };
}

export interface GetCategoriesParams {
  parentId?: string;
  includeHidden?: boolean;
}

export interface GetCategoriesResponse {
  status: 'success';
  message: string;
  data: {
    categories: Category[];
  };
}

export interface GetCategoryResponse {
  status: 'success';
  message: string;
  data: {
    category: Category;
  };
}

export interface UpdateCategoryRequest {
  name?: string;
  nameEn?: string;
  description?: string;
  descriptionEn?: string;
  price?: number;
  cost?: number;
  cashbackPercentage?: number;
  isEnabledCashback?: boolean;
  isHidden?: boolean;
  isParent?: boolean;
  parentId?: string | null;
}

export interface UpdateCategoryResponse {
  status: 'success';
  message: string;
}

export interface ToggleVisibilityRequest {
  isHidden: boolean;
}

export interface ToggleVisibilityResponse {
  status: 'success';
  message: string;
}

export interface DeleteCategoryResponse {
  status: 'success';
  message: string;
}

export const adminApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Category
    createCategory: builder.mutation<CreateCategoryResponse, CreateCategoryRequest>({
      query: (data) => ({
        url: '/admin/categories',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Categories'],
    }),

    // Get All Categories (admin) – اسم مختلف عن catalog/getCategories لئلا يلغيه overrideExisting
    getAdminCategories: builder.query<GetCategoriesResponse, GetCategoriesParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params?.parentId) searchParams.append('parentId', params.parentId);
        if (params?.includeHidden) searchParams.append('includeHidden', params.includeHidden.toString());

        const queryString = searchParams.toString();
        return {
          url: `/admin/categories${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Categories'],
    }),

    // Get Single Category (admin) – اسم مختلف عن catalog/getCategory لئلا يلغيه overrideExisting
    getAdminCategory: builder.query<GetCategoryResponse, string>({
      query: (categoryId) => ({
        url: `/admin/categories/${categoryId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, categoryId) => [{ type: 'Category', id: categoryId }],
    }),

    // Update Category
    updateCategory: builder.mutation<UpdateCategoryResponse, { categoryId: string; data: UpdateCategoryRequest }>({
      query: ({ categoryId, data }) => ({
        url: `/admin/categories/${categoryId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { categoryId }) => [
        { type: 'Category', id: categoryId },
        'Categories',
      ],
    }),

    // Toggle Category Visibility
    toggleCategoryVisibility: builder.mutation<ToggleVisibilityResponse, { categoryId: string; isHidden: boolean }>({
      query: ({ categoryId, isHidden }) => ({
        url: `/admin/categories/${categoryId}/visibility`,
        method: 'PATCH',
        body: { isHidden },
      }),
      invalidatesTags: (_result, _error, { categoryId }) => [
        { type: 'Category', id: categoryId },
        'Categories',
      ],
    }),

    // Delete Category
    deleteCategory: builder.mutation<DeleteCategoryResponse, string>({
      query: (categoryId) => ({
        url: `/admin/categories/${categoryId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Categories'],
    }),
  }),
});

export const {
  useCreateCategoryMutation,
  useGetAdminCategoriesQuery,
  useGetAdminCategoryQuery,
  useUpdateCategoryMutation,
  useToggleCategoryVisibilityMutation,
  useDeleteCategoryMutation,
} = adminApi;

// ==================== Products Management ====================

export interface CreateProductRequest {
  categoryId: string;
  name: string;
  description?: string;
  value: string;
}

export interface CreateProductResponse {
  status: 'success';
  message: string;
  data: {
    product: Product;
  };
}

export interface GetProductsParams {
  categoryId?: string;
  status?: 'available' | 'sold';
  page?: number;
  limit?: number;
}

export interface GetProductsResponse {
  status: 'success';
  message: string;
  data: {
    products: Product[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface GetProductResponse {
  status: 'success';
  message: string;
  data: {
    product: Product;
  };
}

export interface UpdateProductRequest {
  name?: string;
  description?: string;
  value?: string;
  status?: 'available' | 'sold';
}

export interface UpdateProductResponse {
  status: 'success';
  message: string;
  data: {
    product: Product;
  };
}

export interface UpdateProductStatusRequest {
  status: 'available' | 'sold';
  userId?: number;
}

export interface UpdateProductStatusResponse {
  status: 'success';
  message: string;
  data: {
    product: Product;
  };
}

export interface DeleteProductResponse {
  status: 'success';
  message: string;
}

export interface GetCategoryProductsResponse {
  status: 'success';
  message: string;
  data: {
    products: Product[];
  };
}

export const adminProductsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Create Product
    createProduct: builder.mutation<CreateProductResponse, CreateProductRequest>({
      query: (data) => ({
        url: '/admin/products',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Products', 'Categories'],
    }),

    // Get All Products
    getProducts: builder.query<GetProductsResponse, GetProductsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params?.categoryId) searchParams.append('categoryId', params.categoryId);
        if (params?.status) searchParams.append('status', params.status);
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());

        const queryString = searchParams.toString();
        return {
          url: `/admin/products${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Products'],
    }),

    // Get Single Product
    getProduct: builder.query<GetProductResponse, string>({
      query: (productId) => ({
        url: `/admin/products/${productId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, productId) => [{ type: 'Product', id: productId }],
    }),

    // Update Product
    updateProduct: builder.mutation<UpdateProductResponse, { productId: string; data: UpdateProductRequest }>({
      query: ({ productId, data }) => ({
        url: `/admin/products/${productId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: 'Product', id: productId },
        'Products',
        'Categories',
      ],
    }),

    // Update Product Status
    updateProductStatus: builder.mutation<UpdateProductStatusResponse, { productId: string; data: UpdateProductStatusRequest }>({
      query: ({ productId, data }) => ({
        url: `/admin/products/${productId}/status`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { productId }) => [
        { type: 'Product', id: productId },
        'Products',
        'Categories',
      ],
    }),

    // Delete Product
    deleteProduct: builder.mutation<DeleteProductResponse, string>({
      query: (productId) => ({
        url: `/admin/products/${productId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Products', 'Categories'],
    }),

    // Get Category Products (Admin)
    getAdminCategoryProducts: builder.query<GetCategoryProductsResponse, string>({
      query: (categoryId) => ({
        url: `/admin/categories/${categoryId}/products`,
        method: 'GET',
      }),
      providesTags: (_result, _error, categoryId) => [{ type: 'Products', id: categoryId }],
    }),
  }),
});

export const {
  useCreateProductMutation,
  useGetProductsQuery,
  useGetProductQuery,
  useUpdateProductMutation,
  useUpdateProductStatusMutation,
  useDeleteProductMutation,
  useGetAdminCategoryProductsQuery,
} = adminProductsApi;

// ==================== Users Management ====================

export interface GetUsersParams {
  page?: number;
  limit?: number;
  isBanned?: boolean;
  isAdmin?: boolean;
  search?: string;
  /** ترتيب حسب: balance | createdAt. الباك اند يجب أن يقبل sortBy و sortOrder */
  sortBy?: 'balance' | 'createdAt';
  /** 1 تصاعدي، -1 تنازلي */
  sortOrder?: 1 | -1;
}

export interface GetUsersResponse {
  status: 'success';
  message: string;
  data: {
    users: User[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface GetUserDetailsParams {
  userId?: string;
  telegramId?: number;
  email?: string;
}

export interface GetUserDetailsResponse {
  status: 'success';
  message: string;
  data: {
    user: User & {
      referredBy?: number;
      apiKeys?: Array<{
        keyId: string;
        label: string;
        createdAt: string;
      }>;
    };
  };
}

export interface ToggleBanRequest {
  isBanned: boolean;
}

export interface ToggleBanResponse {
  status: 'success';
  message: string;
  data: {
    user: User;
  };
}

export interface UpdateRoleRequest {
  isAdmin: boolean;
}

export interface UpdateRoleResponse {
  status: 'success';
  message: string;
  data: {
    user: User;
  };
}

export interface DeleteUserRequest {
  reason?: string;
}

export interface DeleteUserResponse {
  status: 'success';
  message: string;
  data: {
    success: boolean;
    message: string;
    deletedUser: {
      telegramId: number;
      firstName: string;
      lastName?: string;
      email?: string;
      balance: string;
      referralCount?: number;
    };
  };
}

export interface UserStatistics {
  totalUsers: number;
  activeUsers: number;
  bannedUsers: number;
  adminUsers: number;
  telegramUsers: number;
  emailUsers: number;
  bannedPercentage: string;
  adminPercentage: string;
}

export interface GetUserStatisticsResponse {
  status: 'success';
  message: string;
  data: {
    statistics: UserStatistics;
  };
}

export const adminUsersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get All Users
    getUsers: builder.query<GetUsersResponse, GetUsersParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.isBanned !== undefined) searchParams.append('isBanned', params.isBanned.toString());
        if (params?.isAdmin !== undefined) searchParams.append('isAdmin', params.isAdmin.toString());
        if (params?.search) searchParams.append('search', params.search);
        if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params?.sortOrder !== undefined) searchParams.append('sortOrder', params.sortOrder.toString());

        const queryString = searchParams.toString();
        return {
          url: `/admin/users${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Users'],
    }),

    // Get User Details
    getUserDetails: builder.query<GetUserDetailsResponse, GetUserDetailsParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params.userId) searchParams.append('_id', params.userId);
        if (params.telegramId) searchParams.append('telegramId', params.telegramId.toString());
        if (params.email) searchParams.append('email', params.email);

        const queryString = searchParams.toString();
        return {
          url: `/admin/users/details${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, params) => [
        { type: 'User', id: params.userId || params.telegramId?.toString() || params.email || 'unknown' },
      ],
    }),

    // Toggle User Ban Status
    toggleUserBan: builder.mutation<ToggleBanResponse, { userId: string; isBanned: boolean }>({
      query: ({ userId, isBanned }) => ({
        url: `/admin/users/${userId}/ban`,
        method: 'PATCH',
        body: { isBanned },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
      ],
    }),

    // Update User Role
    updateUserRole: builder.mutation<UpdateRoleResponse, { userId: string; isAdmin: boolean }>({
      query: ({ userId, isAdmin }) => ({
        url: `/admin/users/${userId}/role`,
        method: 'PATCH',
        body: { isAdmin },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
      ],
    }),

    // Change User Password
    changePassword: builder.mutation<{ status: string; message: string; data: { success: boolean } }, { userId: string; newPassword: string }>({
      query: ({ userId, newPassword }) => ({
        url: `/admin/users/${userId}/password`,
        method: 'PATCH',
        body: { newPassword },
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
      ],
    }),

    // Delete User
    deleteUser: builder.mutation<DeleteUserResponse, { userId: string; reason?: string }>({
      query: ({ userId, reason }) => ({
        url: `/admin/users/${userId}`,
        method: 'DELETE',
        body: { reason },
      }),
      invalidatesTags: ['Users'],
    }),

    // Get User Statistics
    getUserStatistics: builder.query<GetUserStatisticsResponse, void>({
      query: () => ({
        url: '/admin/users/statistics',
        method: 'GET',
      }),
      providesTags: ['Users'],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useGetUserDetailsQuery,
  useToggleUserBanMutation,
  useUpdateUserRoleMutation,
  useChangePasswordMutation,
  useDeleteUserMutation,
  useGetUserStatisticsQuery,
} = adminUsersApi;

// ==================== Balance Management ====================

export interface AdjustBalanceRequest {
  amount: number;
  type: 'admin_add' | 'admin_deduct';
  reason?: string;
}

export interface AdjustBalanceResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      telegramId: number;
      firstName: string;
      lastName?: string;
      balance: string;
      updatedAt: string;
    };
    transaction: {
      _id: string;
      userId: number;
      type: string;
      amount: number;
      currency: string;
      status: string;
      notes?: string;
      adminId?: number;
      createdAt: string;
    };
  };
}

export interface AdjustReferralBalanceRequest {
  amount: number;
  type: 'admin_add_referral' | 'admin_deduct_referral';
  reason?: string;
}

export interface AdjustReferralBalanceResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      telegramId: number;
      firstName: string;
      lastName?: string;
      referralBalance: string;
      updatedAt: string;
    };
    transaction: {
      _id: string;
      userId: number;
      type: string;
      amount: number;
      currency: string;
      status: string;
      notes?: string;
      adminId?: number;
      createdAt: string;
    };
  };
}

export interface AdjustCashbackBalanceRequest {
  amount: number;
  type: 'admin_add_cashback' | 'admin_deduct_cashback';
  reason?: string;
}

export interface AdjustCashbackBalanceResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      telegramId: number;
      firstName: string;
      lastName?: string;
      cashbackBalance: string;
      updatedAt: string;
    };
    transaction: {
      _id: string;
      userId: number;
      type: string;
      amount: number;
      currency: string;
      status: string;
      notes?: string;
      adminId?: number;
      createdAt: string;
    };
  };
}

export interface GetUserBalanceInfoParams {
  telegramId?: number;
  email?: string;
}

export interface GetUserBalanceInfoResponse {
  status: 'success';
  message: string;
  data: {
    user: {
      telegramId: number;
      firstName: string;
      lastName?: string;
      email?: string;
      balance: string;
      referralBalance: string;
      cashbackBalance: string;
      referralCount?: number;
      isBanned: boolean;
      createdAt: string;
    };
  };
}

export interface ProcessReferralAdjustmentRequest {
  action: string;
  data: Record<string, unknown>;
  reason?: string;
}

export interface ProcessReferralAdjustmentResponse {
  status: 'success';
  message: string;
  data: {
    success: boolean;
    message: string;
    data: {
      action: string;
      processedUsers?: number;
      totalAmount?: number;
    };
  };
}

export const adminBalanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Adjust User Main Balance
    adjustMainBalance: builder.mutation<AdjustBalanceResponse, { userId: string; data: AdjustBalanceRequest }>({
      query: ({ userId, data }) => ({
        url: `/admin/users/${userId}/balance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
        'Balance',
      ],
    }),

    // Adjust User Referral Balance
    adjustReferralBalance: builder.mutation<AdjustReferralBalanceResponse, { userId: string; data: AdjustReferralBalanceRequest }>({
      query: ({ userId, data }) => ({
        url: `/admin/users/${userId}/referral-balance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
        'Balance',
      ],
    }),

    // Adjust User Cashback Balance
    adjustCashbackBalance: builder.mutation<AdjustCashbackBalanceResponse, { userId: string; data: AdjustCashbackBalanceRequest }>({
      query: ({ userId, data }) => ({
        url: `/admin/users/${userId}/cashback-balance`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { userId }) => [
        { type: 'User', id: userId },
        'Users',
        'Balance',
      ],
    }),

    // Get User Balance Info
    getUserBalanceInfo: builder.query<GetUserBalanceInfoResponse, GetUserBalanceInfoParams>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params.telegramId) searchParams.append('telegramId', params.telegramId.toString());
        if (params.email) searchParams.append('email', params.email);

        const queryString = searchParams.toString();
        return {
          url: `/admin/users/balance-info${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: (_result, _error, params) => [
        { type: 'User', id: params.telegramId?.toString() || params.email || 'unknown' },
        'Balance',
      ],
    }),

    // Process Referral Adjustment
    processReferralAdjustment: builder.mutation<ProcessReferralAdjustmentResponse, ProcessReferralAdjustmentRequest>({
      query: (data) => ({
        url: '/admin/referral-adjustments',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Users', 'Balance'],
    }),
  }),
});

export const {
  useAdjustMainBalanceMutation,
  useAdjustReferralBalanceMutation,
  useAdjustCashbackBalanceMutation,
  useGetUserBalanceInfoQuery,
  useProcessReferralAdjustmentMutation,
} = adminBalanceApi;

// ==================== System Configuration ====================

export interface GetExchangeRateResponse {
  status: 'success';
  message: string;
  data: {
    rate: {
      usdToSypExchangeRate: number;
    };
  };
}

export interface UpdateExchangeRateRequest {
  usdToSypExchangeRate: number;
}

export interface UpdateExchangeRateResponse {
  status: 'success';
  message: string;
  data: {
    rate: {
      usdToSypExchangeRate: number;
    };
  };
}

export interface ProfitMargins {
  smspool?: {
    profitMargin: number;
  };
  pvadeals?: {
    profitMarginTemp: number;
    profitMarginPrem: number;
  };
  durianRcs?: {
    profitMargin: number;
  };
  proxylte?: {
    profitMargin: number;
  };
  proxyline?: {
    profitMarginBase: number;
    profitMarginDedicated: number;
    profitMarginShared: number;
  };
  proxycheap?: {
    profitMarginBase: number;
    profitMarginDedicated: number;
    profitMarginPremium: number;
  };
  textverified?: {
    profitMargin: number;
    profitMarginMonthly: number;
    profitMarginVoice: number;
    profitMarginReuse: number;
  };
}

export interface GetAllProfitMarginsResponse {
  status: 'success';
  message: string;
  data: {
    margins: ProfitMargins;
  };
}

export interface GetServiceProfitMarginResponse {
  status: 'success';
  message: string;
  data: {
    margin: {
      serviceName: string;
      profitMargins: Record<string, number>;
    };
  };
}

export type ServiceName = 'smspool' | 'pvadeals' | 'durianRcs' | 'proxylte' | 'proxyline' | 'proxycheap' | 'textverified';

export interface UpdateServiceProfitMarginRequest {
  profitMargin?: number;
  profitMarginTemp?: number;
  profitMarginPrem?: number;
  profitMarginBase?: number;
  profitMarginDedicated?: number;
  profitMarginShared?: number;
  profitMarginPremium?: number;
  profitMarginVoice?: number;
  profitMarginReuse?: number;
}

export interface UpdateServiceProfitMarginResponse {
  status: 'success';
  message: string;
  data: {
    margin: {
      serviceName: string;
      profitMargins: Record<string, number>;
    };
  };
}

export const adminSystemConfigApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get USD Exchange Rate
    getExchangeRate: builder.query<GetExchangeRateResponse, void>({
      query: () => ({
        url: '/admin/system-configuration/exchange-rate',
        method: 'GET',
      }),
      providesTags: ['SystemConfig'],
    }),

    // Update USD Exchange Rate
    updateExchangeRate: builder.mutation<UpdateExchangeRateResponse, UpdateExchangeRateRequest>({
      query: (data) => ({
        url: '/admin/system-configuration/exchange-rate',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['SystemConfig'],
    }),

    // Get All Service Profit Margins
    getAllProfitMargins: builder.query<GetAllProfitMarginsResponse, void>({
      query: () => ({
        url: '/admin/system-configuration/profit-margins',
        method: 'GET',
      }),
      providesTags: ['SystemConfig'],
    }),

    // Get Service Profit Margin
    getServiceProfitMargin: builder.query<GetServiceProfitMarginResponse, ServiceName>({
      query: (serviceName) => ({
        url: `/admin/system-configuration/profit-margins/${serviceName}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, serviceName) => [{ type: 'SystemConfig', id: serviceName }],
    }),

    // Update Service Profit Margin
    updateServiceProfitMargin: builder.mutation<UpdateServiceProfitMarginResponse, { serviceName: ServiceName; data: UpdateServiceProfitMarginRequest }>({
      query: ({ serviceName, data }) => ({
        url: `/admin/system-configuration/profit-margins/${serviceName}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { serviceName }) => [
        { type: 'SystemConfig', id: serviceName },
        'SystemConfig',
      ],
    }),
  }),
});

export const {
  useGetExchangeRateQuery,
  useUpdateExchangeRateMutation,
  useGetAllProfitMarginsQuery,
  useGetServiceProfitMarginQuery,
  useUpdateServiceProfitMarginMutation,
} = adminSystemConfigApi;

// ==================== External API Keys Management ====================

export type ExternalServiceName = 'smspool' | 'pvadeals' | 'durianRcs' | 'sms_man' | 'proxylte' | 'proxyline' | 'proxycheap' | 'cryptomus' | 'iproyal';

export interface ExternalApiKeyStatus {
  hasApiKey: boolean;
  hasApiSecret: boolean;
  hasUsername: boolean;
  apiKeyConfigured: boolean;
  apiSecretConfigured: boolean;
  usernameConfigured: boolean;
}

export interface GetAllExternalApiKeysStatusResponse {
  status: 'success';
  message: string;
  data: {
    keysStatus: Record<ExternalServiceName, ExternalApiKeyStatus>;
  };
}

export interface ExternalApiKeyConfig {
  serviceName: ExternalServiceName;
  apiKey: string | null;
  apiSecret: string | null;
  username: string | null;
}

export interface GetExternalApiKeyResponse {
  status: 'success';
  message: string;
  data: {
    keyConfig: ExternalApiKeyConfig;
  };
}

export interface UpdateExternalApiKeyRequest {
  apiKey?: string | null;
  apiSecret?: string | null;
  username?: string | null;
}

export interface UpdateExternalApiKeyResponse {
  status: 'success';
  message: string;
  data: {
    keyConfig: ExternalApiKeyConfig;
  };
}

export const adminExternalApiKeysApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get All External API Keys Status
    getAllExternalApiKeysStatus: builder.query<GetAllExternalApiKeysStatusResponse, void>({
      query: () => ({
        url: '/admin/external-api-keys',
        method: 'GET',
      }),
      providesTags: ['ExternalApiKeys'],
    }),

    // Get External API Key
    getExternalApiKey: builder.query<GetExternalApiKeyResponse, ExternalServiceName>({
      query: (serviceName) => ({
        url: `/admin/external-api-keys/${serviceName}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, serviceName) => [{ type: 'ExternalApiKeys', id: serviceName }],
    }),

    // Update External API Key
    updateExternalApiKey: builder.mutation<UpdateExternalApiKeyResponse, { serviceName: ExternalServiceName; data: UpdateExternalApiKeyRequest }>({
      query: ({ serviceName, data }) => ({
        url: `/admin/external-api-keys/${serviceName}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { serviceName }) => [
        { type: 'ExternalApiKeys', id: serviceName },
        'ExternalApiKeys',
      ],
    }),
  }),
});

export const {
  useGetAllExternalApiKeysStatusQuery,
  useGetExternalApiKeyQuery,
  useUpdateExternalApiKeyMutation,
} = adminExternalApiKeysApi;

// ==================== Payment Gateway Management ====================

export interface PayeerConfig {
  accountNumber: string | null;
  merchantId: string | null;
  secretKey: string | null;
  minAmount?: number;
}

export interface CryptoConfig {
  walletAddress: string | null;
  platformName: string | null;
  merchantId: string | null;
  minAmount?: number;
}

export interface SyriaTelConfig {
  receptionCodes: string[];
  minAmount?: number;
}

export interface ShamCashConfig {
  receptionNumber: string | null;
  minAmount?: number;
}

export interface BinanceConfig {
  enabled: boolean;
  apiKey: string | null;
  secretKey: string | null;
  walletAddress: string | null;
  minAmount: number;
}

export interface CoinExConfig {
  enabled: boolean;
  accessId: string | null;
  secretKey: string | null;
  adminEmail: string | null;
  coin: string;
  minAmount: number;
}

export interface OxapayConfig {
  enabled: boolean;
  apiKey: string | null;
  minAmount: number;
}

export interface GetAllPaymentGatewaysResponse {
  status: 'success';
  message: string;
  data: {
    configs: {
      payeer: PayeerConfig;
      crypto: CryptoConfig;
      syriatelCash: SyriaTelConfig;
      shamCash: ShamCashConfig;
      binance: BinanceConfig;
      coinex: CoinExConfig;
      oxapay: OxapayConfig;
    };
  };
}

export interface GetPaymentGatewayResponse<T> {
  status: 'success';
  message: string;
  data: {
    config: T;
  };
}

export interface UpdatePayeerRequest {
  accountNumber?: string | null;
  merchantId?: string | null;
  secretKey?: string | null;
  minAmount?: number;
}

export interface UpdateCryptoRequest {
  walletAddress?: string | null;
  platformName?: string | null;
  merchantId?: string | null;
  minAmount?: number;
}

export interface UpdateSyriaTelRequest {
  receptionCodes?: string[];
  minAmount?: number;
}

export interface UpdateShamCashRequest {
  receptionNumber?: string | null;
  minAmount?: number;
}

export interface UpdateCoinExRequest {
  enabled?: boolean;
  accessId?: string | null;
  secretKey?: string | null;
  adminEmail?: string | null;
  coin?: string;
  minAmount?: number;
}

export interface UpdateOxapayRequest {
  enabled?: boolean;
  apiKey?: string | null;
  minAmount?: number;
}

export interface UpdateBinanceRequest {
  enabled?: boolean;
  apiKey?: string | null;
  secretKey?: string | null;
  walletAddress?: string | null;
  minAmount?: number;
}

export const adminPaymentGatewaysApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get All Payment Gateway Configurations
    getAllPaymentGateways: builder.query<GetAllPaymentGatewaysResponse, void>({
      query: () => ({
        url: '/admin/payment-gateways',
        method: 'GET',
      }),
      providesTags: ['PaymentGateways'],
    }),

    // Get Payeer Configuration
    getPayeerConfig: builder.query<GetPaymentGatewayResponse<PayeerConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/payeer',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'payeer' }],
    }),

    // Update Payeer Configuration
    updatePayeerConfig: builder.mutation<GetPaymentGatewayResponse<PayeerConfig>, UpdatePayeerRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/payeer',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'payeer' }, 'PaymentGateways'],
    }),

    // Get Crypto Configuration
    getCryptoConfig: builder.query<GetPaymentGatewayResponse<CryptoConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/crypto',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'crypto' }],
    }),

    // Update Crypto Configuration
    updateCryptoConfig: builder.mutation<GetPaymentGatewayResponse<CryptoConfig>, UpdateCryptoRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/crypto',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'crypto' }, 'PaymentGateways'],
    }),

    // Get SyriaTel Configuration
    getSyriaTelConfig: builder.query<GetPaymentGatewayResponse<SyriaTelConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/syriatel',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'syriatel' }],
    }),

    // Update SyriaTel Configuration
    updateSyriaTelConfig: builder.mutation<GetPaymentGatewayResponse<SyriaTelConfig>, UpdateSyriaTelRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/syriatel',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'syriatel' }, 'PaymentGateways'],
    }),

    // Get Sham Cash Configuration
    getShamCashConfig: builder.query<GetPaymentGatewayResponse<ShamCashConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/sham-cash',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'shamCash' }],
    }),

    // Update Sham Cash Configuration
    updateShamCashConfig: builder.mutation<GetPaymentGatewayResponse<ShamCashConfig>, UpdateShamCashRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/sham-cash',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'shamCash' }, 'PaymentGateways'],
    }),

    // Get Binance Configuration
    getBinanceConfig: builder.query<GetPaymentGatewayResponse<BinanceConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/binance',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'binance' }],
    }),

    // Update Binance Configuration
    updateBinanceConfig: builder.mutation<GetPaymentGatewayResponse<BinanceConfig>, UpdateBinanceRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/binance',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'binance' }, 'PaymentGateways'],
    }),

    // Get CoinEx Configuration
    getCoinExConfig: builder.query<GetPaymentGatewayResponse<CoinExConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/coinex',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'coinex' }],
    }),

    // Update CoinEx Configuration
    updateCoinExConfig: builder.mutation<GetPaymentGatewayResponse<CoinExConfig>, UpdateCoinExRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/coinex',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'coinex' }, 'PaymentGateways'],
    }),

    // Get Oxapay Configuration
    getOxapayConfig: builder.query<GetPaymentGatewayResponse<OxapayConfig>, void>({
      query: () => ({
        url: '/admin/payment-gateways/oxapay',
        method: 'GET',
      }),
      providesTags: [{ type: 'PaymentGateways', id: 'oxapay' }],
    }),

    // Update Oxapay Configuration
    updateOxapayConfig: builder.mutation<GetPaymentGatewayResponse<OxapayConfig>, UpdateOxapayRequest>({
      query: (data) => ({
        url: '/admin/payment-gateways/oxapay',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: [{ type: 'PaymentGateways', id: 'oxapay' }, 'PaymentGateways'],
    }),
  }),
});

export const {
  useGetAllPaymentGatewaysQuery,
  useGetPayeerConfigQuery,
  useUpdatePayeerConfigMutation,
  useGetCryptoConfigQuery,
  useUpdateCryptoConfigMutation,
  useGetSyriaTelConfigQuery,
  useUpdateSyriaTelConfigMutation,
  useGetShamCashConfigQuery,
  useUpdateShamCashConfigMutation,
  useGetBinanceConfigQuery,
  useUpdateBinanceConfigMutation,
  useGetCoinExConfigQuery,
  useUpdateCoinExConfigMutation,
  useGetOxapayConfigQuery,
  useUpdateOxapayConfigMutation,
} = adminPaymentGatewaysApi;

// ==================== Cashback Settings ====================

export interface CashbackSettings {
  isEnabled: boolean;
  defaultPercentage: number;
  services: {
    smspool?: number;
    pvadeals?: number;
    durianRcs?: number;
    proxyline?: number;
    proxycheap?: {
      basic?: number;
      dedicated?: number;
      premium?: number;
    };
    warehouse?: number;
  };
}

export interface GetCashbackSettingsResponse {
  status: 'success';
  message: string;
  data: {
    settings: CashbackSettings;
  };
}

export interface UpdateCashbackSettingsRequest {
  isEnabled?: boolean;
  defaultPercentage?: number;
  services?: {
    smspool?: number;
    pvadeals?: number;
    durianRcs?: number;
    proxyline?: number;
    proxycheap?: {
      basic?: number;
      dedicated?: number;
      premium?: number;
    };
    warehouse?: number;
  };
}

export interface UpdateCashbackSettingsResponse {
  status: 'success';
  message: string;
  data: {
    settings: CashbackSettings;
  };
}

export interface ToggleCashbackRequest {
  isEnabled: boolean;
}

export interface ToggleCashbackResponse {
  status: 'success';
  message: string;
  data: {
    settings: CashbackSettings;
  };
}

export type CashbackServiceName = 'smspool' | 'pvadeals' | 'durianRcs' | 'proxyline' | 'warehouse';

export interface UpdateServiceCashbackRequest {
  percentage: number;
}

export interface UpdateServiceCashbackResponse {
  status: 'success';
  message: string;
  data: {
    settings: CashbackSettings;
  };
}

export interface UpdateProxyCheapCashbackRequest {
  basic?: number;
  dedicated?: number;
  premium?: number;
}

export interface UpdateProxyCheapCashbackResponse {
  status: 'success';
  message: string;
  data: {
    settings: CashbackSettings;
  };
}

export const adminCashbackSettingsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get Cashback Settings
    getCashbackSettings: builder.query<GetCashbackSettingsResponse, void>({
      query: () => ({
        url: '/admin/cashback-settings',
        method: 'GET',
      }),
      providesTags: ['CashbackSettings'],
    }),

    // Update Cashback Settings
    updateCashbackSettings: builder.mutation<UpdateCashbackSettingsResponse, UpdateCashbackSettingsRequest>({
      query: (data) => ({
        url: '/admin/cashback-settings',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CashbackSettings'],
    }),

    // Toggle Cashback System
    toggleCashbackSystem: builder.mutation<ToggleCashbackResponse, ToggleCashbackRequest>({
      query: (data) => ({
        url: '/admin/cashback-settings/toggle',
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: ['CashbackSettings'],
    }),

    // Update Service Cashback Percentage
    updateServiceCashback: builder.mutation<UpdateServiceCashbackResponse, { serviceName: CashbackServiceName; data: UpdateServiceCashbackRequest }>({
      query: ({ serviceName, data }) => ({
        url: `/admin/cashback-settings/services/${serviceName}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CashbackSettings'],
    }),

    // Update ProxyCheap Cashback Percentages
    updateProxyCheapCashback: builder.mutation<UpdateProxyCheapCashbackResponse, UpdateProxyCheapCashbackRequest>({
      query: (data) => ({
        url: '/admin/cashback-settings/services/proxycheap',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['CashbackSettings'],
    }),
  }),
});

export const {
  useGetCashbackSettingsQuery,
  useUpdateCashbackSettingsMutation,
  useToggleCashbackSystemMutation,
  useUpdateServiceCashbackMutation,
  useUpdateProxyCheapCashbackMutation,
} = adminCashbackSettingsApi;

// ==================== Transactions Management ====================

import { Transaction } from '../../types/api';

export interface GetTransactionsParams {
  page?: number;
  limit?: number;
  type?: string;
  status?: 'pending' | 'completed' | 'failed' | 'reversed';
  paymentMethod?: string;
  userId?: string | number;
  currency?: string;
  dateFrom?: string;
  dateTo?: string;
  minAmount?: number;
  maxAmount?: number;
  sortBy?: 'createdAt' | 'updatedAt' | 'amount' | 'type' | 'status';
  sortOrder?: -1 | 1;
}

export interface GetTransactionsResponse {
  status: 'success';
  message: string;
  data: {
    transactions: Transaction[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
    filters?: Record<string, unknown>;
  };
}

export const adminTransactionsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get All Transactions
    getTransactions: builder.query<GetTransactionsResponse, GetTransactionsParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.type) searchParams.append('type', params.type);
        if (params?.status) searchParams.append('status', params.status);
        if (params?.paymentMethod) searchParams.append('paymentMethod', params.paymentMethod);
        if (params?.userId) searchParams.append('userId', params.userId.toString());
        if (params?.currency) searchParams.append('currency', params.currency);
        if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params?.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params?.minAmount !== undefined) searchParams.append('minAmount', params.minAmount.toString());
        if (params?.maxAmount !== undefined) searchParams.append('maxAmount', params.maxAmount.toString());
        if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params?.sortOrder !== undefined) searchParams.append('sortOrder', params.sortOrder.toString());

        const queryString = searchParams.toString();
        return {
          url: `/admin/transactions${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Transactions'],
    }),
  }),
});

export const {
  useGetTransactionsQuery,
} = adminTransactionsApi;

// ==================== Admin Numbers & Proxies ====================

export interface AdminNumberItem {
  _id: string;
  sourceTable: 'permanentnumbers' | 'smspooltemporarynumbers' | 'duriantemporarynumbers' | string;
  numberType: 'permanent' | 'temporary' | string;
  userId: string | number;
  number: string;
  serviceProvider?: string;
  serviceName?: string;
  countryName?: string;
  externalServiceId?: string;
  price?: number;
  status?: string;
  isTemporary?: boolean;
  code?: string | null;
  isCodeReceived?: boolean;
  purchasedAt?: string;
  expiresAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetAdminNumbersParams {
  page?: number;
  limit?: number;
  sourceTable?: 'all' | 'permanent' | 'smspool_temp' | 'durian_temp';
  userId?: string | number;
  status?: string;
  serviceProvider?: string;
  isTemporary?: boolean;
  serviceName?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'purchasedAt' | 'expiresAt' | 'price' | 'status' | 'number';
  sortOrder?: -1 | 1;
}

export interface GetAdminNumbersResponse {
  status: 'success';
  message: string;
  data: {
    numbers: AdminNumberItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface AdminProxyItem {
  _id: string;
  userId: string | number;
  serverId: 1 | 2 | 3 | 4;
  ipAddress: string;
  port: number;
  port_socks?: number;
  price?: number;
  username?: string;
  password?: string;
  serviceProvider?: string;
  externalServiceId?: string;
  expiresAt?: string;
  purchasedAt?: string;
  status?: 'active' | 'expired' | 'cancelled' | string;
  location?: string;
  city?: string;
  type?: string;
  carrier?: string | null;
  autoExtend?: boolean;
  productName?: string;
  planName?: string;
  productId?: string;
  planId?: string;
  locationId?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface GetAdminProxiesParams {
  page?: number;
  limit?: number;
  userId?: string | number;
  serverId?: 1 | 2 | 3 | 4;
  status?: 'active' | 'expired' | 'cancelled';
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'purchasedAt' | 'expiresAt' | 'price' | 'status';
  sortOrder?: -1 | 1;
}

export interface GetAdminProxiesResponse {
  status: 'success';
  message: string;
  data: {
    proxies: AdminProxyItem[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export const adminAssetsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAdminNumbers: builder.query<GetAdminNumbersResponse, GetAdminNumbersParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.sourceTable) searchParams.append('sourceTable', params.sourceTable);
        if (params?.userId !== undefined) searchParams.append('userId', params.userId.toString());
        if (params?.status) searchParams.append('status', params.status);
        if (params?.serviceProvider) searchParams.append('serviceProvider', params.serviceProvider);
        if (params?.isTemporary !== undefined) searchParams.append('isTemporary', params.isTemporary.toString());
        if (params?.serviceName) searchParams.append('serviceName', params.serviceName);
        if (params?.search) searchParams.append('search', params.search);
        if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params?.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params?.sortOrder !== undefined) searchParams.append('sortOrder', params.sortOrder.toString());
        const queryString = searchParams.toString();
        return {
          url: `/admin/numbers${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['PermanentNumbers', 'TemporaryNumbers'],
    }),
    getAdminProxies: builder.query<GetAdminProxiesResponse, GetAdminProxiesParams | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();
        if (params?.page) searchParams.append('page', params.page.toString());
        if (params?.limit) searchParams.append('limit', params.limit.toString());
        if (params?.userId !== undefined) searchParams.append('userId', params.userId.toString());
        if (params?.serverId) searchParams.append('serverId', params.serverId.toString());
        if (params?.status) searchParams.append('status', params.status);
        if (params?.search) searchParams.append('search', params.search);
        if (params?.dateFrom) searchParams.append('dateFrom', params.dateFrom);
        if (params?.dateTo) searchParams.append('dateTo', params.dateTo);
        if (params?.sortBy) searchParams.append('sortBy', params.sortBy);
        if (params?.sortOrder !== undefined) searchParams.append('sortOrder', params.sortOrder.toString());
        const queryString = searchParams.toString();
        return {
          url: `/admin/proxies${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Proxies'],
    }),
  }),
});

export const {
  useGetAdminNumbersQuery,
  useGetAdminProxiesQuery,
} = adminAssetsApi;

// ==================== User Margins Management ====================

export interface UserMarginSearchResult {
  _id: string;
  telegramId?: number;
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
}

export interface UserMarginsData {
  assignedTier?: 'bronze' | 'silver' | 'gold' | 'premium' | null;
  temporaryNumbers: {
    1: number | null;
    4: number | null;
    5: number | null;
    6: number | null;
  };
  proxies: {
    1: number | null;
    2: number | null;
    3: number | null;
  };
  permanentNumbers: {
    1: number | null;
  };
  voice: {
    1: number | null;
  };
  temporaryNumbersReuse: {
    5: number | null;
    6: number | null;
  };
}

export interface GetAllUserMarginsResponse
{
  status: 'success';
  data: {
    users: UserMarginSearchResult[];
  };
}

export interface GetUserMarginsResponse {
  status: 'success';
  data: {
    user: UserMarginSearchResult;
    margins: UserMarginsData;
  };
}

export interface GetAllUserMarginsItem {
  _id: string;
  userId: UserMarginSearchResult;
  assignedTier?: 'bronze' | 'silver' | 'gold' | 'premium' | null;
  temporaryNumbers: {
    1: number | null;
    4: number | null;
    5: number | null;
    6: number | null;
  };
  proxies: {
    1: number | null;
    2: number | null;
    3: number | null;
  };
  permanentNumbers: {
    1: number | null;
  };
  voice: {
    1: number | null;
  };
  temporaryNumbersReuse: {
    5: number | null;
    6: number | null;
  };
}

export interface GetAllUserMarginsResponse {
  status: 'success';
  data: {
    margins: GetAllUserMarginsItem[];
  };
}

export interface UpdateUserMarginRequest {
  type: 'temporaryNumbers' | 'proxies' | 'permanentNumbers' | 'voice' | 'temporaryNumbersReuse';
  serverId: number;
  margin: number | null;
}

export interface UpdateUserMarginResponse {
  status: 'success';
  message: string;
  data: {
    user: UserMarginSearchResult;
    margins: UserMarginsData;
  };
}

// ==================== Margin Tier Types ====================

export interface MarginTierData {
  _id: string;
  name: 'bronze' | 'silver' | 'gold' | 'premium';
  displayName: { ar: string; en: string };
  temporaryNumbers: Record<number, number | null>;
  proxies: Record<number, number | null>;
  permanentNumbers: Record<number, number | null>;
  voice: Record<number, number | null>;
  temporaryNumbersReuse: Record<number, number | null>;
}

export interface MarginTierSectionValues {
  [serverId: number]: number | null;
}

export interface UpdateMarginTierRequest {
  temporaryNumbers?: MarginTierSectionValues;
  proxies?: MarginTierSectionValues;
  permanentNumbers?: MarginTierSectionValues;
  voice?: MarginTierSectionValues;
  temporaryNumbersReuse?: MarginTierSectionValues;
}

export interface GetMarginTiersResponse {
  status: 'success';
  data: { tiers: MarginTierData[] };
}

export interface AssignTierRequest {
  tier: 'bronze' | 'silver' | 'gold' | 'premium';
}

// ==================== Margin Tiers API ====================

export const adminMarginTiersApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMarginTiers: builder.query<GetMarginTiersResponse, void>({
      query: () => ({
        url: '/admin/margin-tiers',
        method: 'GET',
      }),
      providesTags: ['MarginTiers'],
    }),
    updateMarginTier: builder.mutation<void, { tierName: string; data: UpdateMarginTierRequest }>({
      query: ({ tierName, data }) => ({
        url: `/admin/margin-tiers/${tierName}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['MarginTiers'],
    }),
    assignTierToUser: builder.mutation<UpdateUserMarginResponse, { userId: string; tier: 'bronze' | 'silver' | 'gold' }>({
      query: ({ userId, tier }) => ({
        url: `/admin/user-margins/${userId}/assign-tier`,
        method: 'PUT',
        body: { tier },
      }),
      invalidatesTags: (_result, _error, { userId }) => ['UserMargins', { type: 'UserMargins', id: userId } as const],
    }),
    removeUserTier: builder.mutation<void, string>({
      query: (userId) => ({
        url: `/admin/user-margins/${userId}/assign-tier`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, userId) => ['UserMargins', { type: 'UserMargins', id: userId } as const],
    }),
  }),
});

export const {
  useGetMarginTiersQuery,
  useUpdateMarginTierMutation,
  useAssignTierToUserMutation,
  useRemoveUserTierMutation,
} = adminMarginTiersApi;

// ==================== User Margins API ====================

export const adminUserMarginsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAllUserMargins: builder.query<GetAllUserMarginsResponse, void>({
      query: () => ({
        url: '/admin/user-margins',
        method: 'GET',
      }),
      providesTags: ['UserMargins'],
    }),
    searchUserMargins: builder.query<SearchUserMarginsResponse, string>({
      query: (q) => ({
        url: `/admin/user-margins/search?q=${encodeURIComponent(q)}`,
        method: 'GET',
      }),
    }),
    getUserMargins: builder.query<GetUserMarginsResponse, string>({
      query: (userId) => ({
        url: `/admin/user-margins/${userId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, userId) => [{ type: 'UserMargins', id: userId }],
    }),
    updateUserMargin: builder.mutation<UpdateUserMarginResponse, { userId: string; data: UpdateUserMarginRequest }>({
      query: ({ userId, data }) => ({
        url: `/admin/user-margins/${userId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { userId }) => [{ type: 'UserMargins', id: userId }],
    }),
  }),
});

export const {
  useGetAllUserMarginsQuery,
  useSearchUserMarginsQuery,
  useLazySearchUserMarginsQuery,
  useGetUserMarginsQuery,
  useUpdateUserMarginMutation,
} = adminUserMarginsApi;

// ==================== Maintenance Mode ====================

export interface MaintenanceStatus {
  mode: 'none' | 'comprehensive' | 'bot_only' | 'website_only';
  message: string | null;
}

export interface GetMaintenanceResponse {
  status: 'success';
  data: {
    maintenance: MaintenanceStatus;
  };
}

export interface UpdateMaintenanceRequest {
  mode: 'none' | 'comprehensive' | 'bot_only' | 'website_only';
  message?: string;
}

export interface UpdateMaintenanceResponse {
  status: 'success';
  message: string;
  data: {
    maintenance: MaintenanceStatus;
  };
}

export const adminMaintenanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getMaintenanceStatus: builder.query<GetMaintenanceResponse, void>({
      query: () => ({
        url: '/admin/maintenance',
        method: 'GET',
      }),
      providesTags: ['Maintenance'],
    }),
    updateMaintenanceMode: builder.mutation<UpdateMaintenanceResponse, UpdateMaintenanceRequest>({
      query: (data) => ({
        url: '/admin/maintenance',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Maintenance'],
    }),
  }),
});

export const {
  useGetMaintenanceStatusQuery,
  useUpdateMaintenanceModeMutation,
} = adminMaintenanceApi;

// ==================== Server-specific Maintenance ====================

export interface GetDisabledServersResponse {
  status: 'success';
  message: string;
  data: {
    disabledServers: string[];
  };
}

export interface ToggleServerDisabledRequest {
  disabled: boolean;
}

export interface ToggleServerDisabledResponse {
  status: 'success';
  message: string;
  data: {
    disabledServers: string[];
  };
}

export const adminServerMaintenanceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getDisabledServers: builder.query<GetDisabledServersResponse, void>({
      query: () => ({
        url: 'servers/disabled',
        method: 'GET',
      }),
      providesTags: ['DisabledServers'],
    }),
    toggleServerDisabled: builder.mutation<ToggleServerDisabledResponse, { serverId: string; data: ToggleServerDisabledRequest }>({
      query: ({ serverId, data }) => ({
        url: `/admin/servers/${serverId}/disabled`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['DisabledServers'],
    }),
  }),
});

export const {
  useGetDisabledServersQuery,
  useToggleServerDisabledMutation,
} = adminServerMaintenanceApi;

// ==================== System Webhook Secret ====================

export interface GetSystemWebhookSecretResponse {
  status: 'success';
  data: {
    configured: boolean;
  };
}

export interface UpdateSystemWebhookSecretRequest {
  secret: string;
}

export interface UpdateSystemWebhookSecretResponse {
  status: 'success';
  message: string;
  data: {
    configured: boolean;
  };
}

export const adminSystemWebhookSecretApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSystemWebhookSecret: builder.query<GetSystemWebhookSecretResponse, void>({
      query: () => ({
        url: '/admin/system-webhook-secret',
        method: 'GET',
      }),
      providesTags: ['SystemWebhookSecret'],
    }),
    updateSystemWebhookSecret: builder.mutation<UpdateSystemWebhookSecretResponse, UpdateSystemWebhookSecretRequest>(
      {
        query: (data) => ({
          url: '/admin/system-webhook-secret',
          method: 'PUT',
          body: data,
        }),
        invalidatesTags: ['SystemWebhookSecret'],
      }
    ),
  }),
});

export const {
  useGetSystemWebhookSecretQuery,
  useUpdateSystemWebhookSecretMutation,
} = adminSystemWebhookSecretApi;

// ==================== Hide Products Visibility ====================

export interface HideProductsStatus {
  bot: boolean;
  website: boolean;
}

export interface HideSSNStatus {
  bot: boolean;
  website: boolean;
}

export interface GetHideProductsResponse {
  status: 'success';
  data: {
    hideProducts: HideProductsStatus;
    hideSSN: HideSSNStatus;
    ssnCategoryId: string[];
  };
}

export interface UpdateHideProductsResponse {
  status: 'success';
  message: string;
  data: {
    hideProducts: HideProductsStatus;
    hideSSN: HideSSNStatus;
    ssnCategoryId: string[];
  };
}

export const adminHideProductsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHideProductsStatus: builder.query<GetHideProductsResponse, void>({
      query: () => ({
        url: '/admin/products/hide-status',
        method: 'GET',
      }),
      providesTags: ['HideProducts'],
    }),
    updateHideProductsStatus: builder.mutation<UpdateHideProductsResponse, UpdateHideProductsRequest>({
      query: (data) => ({
        url: '/admin/products/hide-status',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['HideProducts'],
    }),
  }),
});

export const {
  useGetHideProductsStatusQuery,
  useUpdateHideProductsStatusMutation,
} = adminHideProductsApi;

// ==================== Sham Cash Payments Management ====================

export interface GetShamCashPaymentsParams {
  status?: string;
  page?: number;
  limit?: number;
}

export interface ShamCashPaymentUser {
  _id: string;
  telegramId?: number;
  firstName?: string;
  username?: string;
  email?: string;
}

export interface ShamCashPayment {
  _id: string;
  userId: string;
  transactionId: string;
  amount: number;
  currency: string;
  amountInUSD: number;
  status: 'pending' | 'accepted' | 'rejected';
  source: 'telegram' | 'web';
  telegramChatId?: number;
  notificationMessageId?: number;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
  user?: ShamCashPaymentUser;
}

export interface GetShamCashPaymentsResponse {
  status: string;
  data: {
    payments: ShamCashPayment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface AcceptRejectShamCashPaymentRequest {
  id: string;
}

export interface AcceptRejectShamCashPaymentResponse {
  status: string;
  message: string;
  data: {
    payment: ShamCashPayment;
  };
}

const adminShamCashPaymentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getShamCashPayments: builder.query<GetShamCashPaymentsResponse, GetShamCashPaymentsParams>({
      query: (params) => ({
        url: '/admin/sham-cash-payments',
        method: 'GET',
        params,
      }),
      providesTags: ['ShamCashPayments'],
    }),
    acceptShamCashPayment: builder.mutation<AcceptRejectShamCashPaymentResponse, AcceptRejectShamCashPaymentRequest>({
      query: ({ id }) => ({
        url: `/admin/sham-cash-payments/${id}/accept`,
        method: 'PUT',
      }),
      invalidatesTags: ['ShamCashPayments'],
    }),
    rejectShamCashPayment: builder.mutation<AcceptRejectShamCashPaymentResponse, AcceptRejectShamCashPaymentRequest>({
      query: ({ id }) => ({
        url: `/admin/sham-cash-payments/${id}/reject`,
        method: 'PUT',
      }),
      invalidatesTags: ['ShamCashPayments'],
    }),
  }),
});

export interface GetSyriatelCashPaymentsParams {
  status?: string;
  page?: number;
  limit?: number;
}

export interface SyriatelCashPaymentUser {
  _id: string;
  telegramId?: number;
  firstName?: string;
  username?: string;
  email?: string;
}

export interface SyriatelCashPayment {
  _id: string;
  userId: string;
  transactionId: string;
  amount: number;
  amountInUSD: number;
  status: 'pending' | 'accepted' | 'rejected';
  source: 'telegram' | 'web';
  telegramChatId?: number;
  notificationMessageId?: number;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
  user?: SyriatelCashPaymentUser;
}

export interface GetSyriatelCashPaymentsResponse {
  status: string;
  data: {
    payments: SyriatelCashPayment[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface AcceptRejectSyriatelCashPaymentRequest {
  id: string;
}

export interface AcceptRejectSyriatelCashPaymentResponse {
  status: string;
  message: string;
  data: {
    payment: SyriatelCashPayment;
  };
}

const adminSyriatelCashPaymentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSyriatelCashPayments: builder.query<GetSyriatelCashPaymentsResponse, GetSyriatelCashPaymentsParams>({
      query: (params) => ({
        url: '/admin/syriatel-cash-payments',
        method: 'GET',
        params,
      }),
      providesTags: ['SyriatelCashPayments'],
    }),
    acceptSyriatelCashPayment: builder.mutation<AcceptRejectSyriatelCashPaymentResponse, AcceptRejectSyriatelCashPaymentRequest>({
      query: ({ id }) => ({
        url: `/admin/syriatel-cash-payments/${id}/accept`,
        method: 'PUT',
      }),
      invalidatesTags: ['SyriatelCashPayments'],
    }),
    rejectSyriatelCashPayment: builder.mutation<AcceptRejectSyriatelCashPaymentResponse, AcceptRejectSyriatelCashPaymentRequest>({
      query: ({ id }) => ({
        url: `/admin/syriatel-cash-payments/${id}/reject`,
        method: 'PUT',
      }),
      invalidatesTags: ['SyriatelCashPayments'],
    }),
  }),
});

export const {
  useGetShamCashPaymentsQuery,
  useAcceptShamCashPaymentMutation,
  useRejectShamCashPaymentMutation,
} = adminShamCashPaymentsApi;

export const {
  useGetSyriatelCashPaymentsQuery,
  useAcceptSyriatelCashPaymentMutation,
  useRejectSyriatelCashPaymentMutation,
} = adminSyriatelCashPaymentsApi;
